import React, { useEffect, useState, useMemo } from 'react'
import { navigate } from 'gatsby'
import { useSetRecoilState } from 'recoil'
import { Col, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { Loading } from '/src/components/loading'
import { Button } from '/src/components/common'
import { BillingAddressElement } from '/src/components/commerce/form/elements'
import { cartCountState, cartState } from '/src/hooks/useCart'
import { postRequest } from '/src/httpClient'
import PaymentPayPalPaneForm from './PaymentPayPalPaneForm'
import PaymentCyberSourcePaneForm from './PaymentCyberSourcePaneForm'

export const PaymentPaneForm = ({ cart, user }) => {
  const [message, setMessage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('credit_card')
  const { register, watch, control, setValue, getValues, handleSubmit } = useForm({
    defaultValues: {
      payment_method: 'credit_card'
    }
  })
  const setCartState = useSetRecoilState(cartState)
  const setCartCountState = useSetRecoilState(cartCountState)
  const hasBillingAddress = useMemo(() => getValues('select_address') ? true : false, [getValues('select_address')])

  watch('payment_method')
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'payment_method') {
        setPaymentMethod(value.payment_method)
      }
    })
    return () => {
      subscription.unsubscribe()
      setIsLoading(false)
    }
  }, [watch])

  const onSubmit = (data) => {
    window.dataLayer = window.dataLayer || []
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'checkoutPanePaymentSubmit',
        order: {
          id: cart.id,
          number: cart.attributes.order_number,
          total: cart.attributes.order_total.total.number,
          user: cart.attributes.email,
          order_items: cart.relationships.order_items.data.map((item) => {
            return {
              id: item.id,
              ref: item.attributes.product_reference,
              title: item.attributes.title,
              quantity: item.attributes.quantity,
              total_price: item.attributes.total_price.number,
              unit_price: item.attributes.unit_price.number,
              attributes: item.attributes.variation_attributes?.map((attribute) => {
                return {
                  label: attribute.label,
                  value: attribute.value
                }
              })
            }
          }),
          payment_method: data.payment_method
        },
        store: {
          name: cart.store.name,
          plant_id: cart.store.dsap_plant_id
        }
      })
    }

    if (data.payment_method === 'credit_card') {
      setIsLoading(true)

      // Submit payment request.
      postRequest('/api/payment', {
        data: {
          ...data,
          order_number: cart.attributes.order_number
        }
      }).then(response => {
        if (response) {
          setCartState(false)
          setCartCountState(0)
          navigate(`/checkout/complete/${cart.id}`)
        }
        else {
          setIsLoading(false)
          setMessage('Payment failed, please try again.')
          console.log('payment failed')
        }
      })
    }
    else if (data.payment_method === 'paypal') {
      console.log('paypal submission')
    }
  }

  return (
    <div className="payment-form-container">
      <form id="payment-pane-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-item mb-4">
          <BillingAddressElement 
            label="Billing Address"
            cart={cart}
            user={user}
            control={control} 
            register={register}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
          />
        </div>
        {!hasBillingAddress && (
          <div className='form-item mb-4'>
            <p>Please select billing information before entering payment details.</p>
          </div>
        )}
        <div className="form-item-radios payment-method mb-4">
          <label className="mb-2">Payment Method</label>
          <Row>
            <Col sm={6} md={4}>
              <div className="form-item radio">
                <label>
                  Credit Card
                  <input type="radio" value="credit_card" {...register('payment_method')} />
                </label>
              </div>
            </Col>
            <Col sm={6} md={4}>
              <div className="form-item radio">
                <label>
                  PayPal
                  <input type="radio" value="paypal" {...register('payment_method')} />
                </label>
              </div>
            </Col>
          </Row>
        </div>
        {message && (
          <div className="form-item message mb-4">
            {message}
          </div>
        )}
        {paymentMethod === 'credit_card' && (
          <PaymentCyberSourcePaneForm 
            control={control}
            register={register} />
        )}
        {paymentMethod === 'paypal' && (
          <PaymentPayPalPaneForm 
            cart={cart}
            control={control}
            register={register}
            setValue={setValue}
            getValues={getValues}
            watch={watch} />
        )}

        <Button disabled={isLoading || !hasBillingAddress} variant="primary" type="submit">
          Pay Now
          {isLoading ? <Loading /> : null}
        </Button>
      </form>
    </div>
  )
}

export default PaymentPaneForm
